import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { AbstractModal, BroadcastService, ToasterType } from "@cv-invision/core";
import { ExternalApiService } from "@cv-invision/cv.invision-views.ux";
import { RotateImageService } from "./service/rotate-image.service";

@Component({
	selector: 'cv-invision-ux-rotate-image',
	templateUrl: './rotate-image.component.html',
	styleUrls: [ './rotate-image.component.scss' ],
	providers: [ RotateImageService ]
})
export class RotateImageComponent extends AbstractModal implements OnInit {
	@ViewChild('canvasRotateImage') canvas: ElementRef<HTMLCanvasElement>;
	@ViewChild('canvasImageContainer') canvasImageContainer: ElementRef<HTMLCanvasElement>;
	@ViewChild('displayImage') displayImage: ElementRef<HTMLCanvasElement>;
	currentAlbumImage: any;
	path: string;
	image: any;
	currentDegrees: number;
	showLoading: boolean;
	photos: any;
	order: any;
	loadingData = false;
	previewPhoto: string;
	private interval : number;
	retriesLoadImage = 0;
	photoIsAvailable = true;
	canvasLoaded = false;

	constructor(
		private externalApiService: ExternalApiService,
		private broadcastService: BroadcastService,
		private rotateImageService: RotateImageService
	) {
		super();
	}

	async ngOnInit(): Promise<void> {
		this.currentDegrees = 0;
		this.showLoading = true;
		if (this.data) {
			this.currentAlbumImage = this.data.currentAlbumImage;
			this.path = this.data.jsonPath;
			this.photos = this.data.photos;
			this.order = this.data.order;
			this.previewPhoto = this.currentAlbumImage.thumb;
			this.showLoading = false;
			this.image = new Image();
			this.image.crossOrigin = "anonymous";
			this.image.src = this.currentAlbumImage.src;
			this.image.onload = () => {
				const ctx = this.canvas.nativeElement.getContext('2d');
				const wrh = this.image.width / this.image.height;
				let newWidth = this.canvas.nativeElement.width;
				let newHeight = newWidth / wrh;
				if (newHeight > this.canvas.nativeElement.height) {
					newHeight = this.canvas.nativeElement.height;
					newWidth = newHeight * wrh;
				}
				const xOffset = newWidth < this.canvas.nativeElement.width ? ((this.canvas.nativeElement.width - newWidth) / 2) : 0;
				const yOffset = newHeight < this.canvas.nativeElement.height ? ((this.canvas.nativeElement.height - newHeight) / 2) : 0;

				ctx.drawImage(this.image, xOffset, yOffset, newWidth, newHeight);
				this.setDirectionImageContainer();
				this.showLoading = false;
				this.canvasLoaded = true;
			}
		}
	}

	onDismiss(): void {
		super.onCancel();
	}

	/**
	 * event to update attachment
	 */
	async saveImage(): Promise<void> {
		this.loadingData = true;
		const response = await this.externalApiService.getImageFromBase64(this.canvas.nativeElement.toDataURL());
		const file = new File([ response ], 'rotatedImage.png', {type: "image/png"});
		const photoSelected = this.photos.filter(item => item.id === this.currentAlbumImage.id);
		const updateAttachment = await this.externalApiService.updateAttachment(this.data.order.id, photoSelected[0].id, file).toPromise().catch(e => console.log(e));
		const newAttachment = await this.externalApiService.getAttachmentById(this.order.id, photoSelected[0].id).toPromise().catch(e => console.log(e));
		if (updateAttachment.succeed) {
			setTimeout(async () => {

				this.broadcastService.broadcast({
					name: ToasterType.TOASTER,
					event: {
						type: 'info',
						title: 'Attachment updated',
						message: 'Attachment was updated'
					}
				});
				this.loadingData = false;
				this.onAcceptSuccess(newAttachment);
			}, 2000);
		}
	}

	/**
	 * Event to rotate image to the left
	 */
	rotateLeft(): void {
		if (this.currentDegrees == 0)
			this.currentDegrees = 270;
		else
			this.currentDegrees -= 90;
		this.canvasImageContainer.nativeElement.style.transform = `rotate(${this.currentDegrees}deg)`;
		this.rotateCanvasImage(this.currentDegrees);
	}

	/**
	 * Event to rotate image to the right
	 */
	rotateRight(): void {

		if (this.currentDegrees >= 360) {
			this.currentDegrees = 0
		}
		this.currentDegrees += 90;
		this.canvasImageContainer.nativeElement.style.transform = `rotate(${this.currentDegrees}deg)`;
		this.rotateCanvasImage(this.currentDegrees);
	}

	/**
	 * event to rotate image in specific degrees and render in modal
	 * @param degrees
	 * @private
	 */
	private rotateCanvasImage(degrees: number): void {
		const ctx = this.canvas.nativeElement.getContext("2d");
		if (degrees == 90 || degrees == 270) {
			this.canvas.nativeElement.width = this.image.height;
			this.canvas.nativeElement.height = this.image.width;
		} else {
			this.canvas.nativeElement.width = this.image.width;
			this.canvas.nativeElement.height = this.image.height;
		}

		ctx.clearRect(0, 0, this.canvas.nativeElement.width, this.canvas.nativeElement.height);
		if (degrees == 90 || degrees == 270) {
			ctx.translate(this.image.height / 2, this.image.width / 2);
		} else {
			ctx.translate(this.image.width / 2, this.image.height / 2);
		}
		ctx.rotate(degrees * Math.PI / 180);
		ctx.drawImage(this.image, -this.image.width / 2, -this.image.height / 2);

	}

	/**
	 * Event to check if an image is in portrait or landscape
	 */
	setDirectionImageContainer() {
		if (this.canvas.nativeElement.width > this.canvas.nativeElement.height) {
			this.canvasImageContainer.nativeElement.classList.remove('portrait-image');
			this.canvasImageContainer.nativeElement.classList.add('landscape-image');
		} else {
			this.canvasImageContainer.nativeElement.classList.remove('landscape-image');
			this.canvasImageContainer.nativeElement.classList.add('portrait-image');
		}
	}


	/**
	 * Callback after image is loaded
	 */
	onImageLoad(): void {
		this.showLoading = false;
		if (this.interval) {
			clearInterval(this.interval);
		}
	}

	/**
	 * Callback when the image has error during loading
	 */
	onImageError(): void {
		this.previewPhoto = null;
		if (this.interval) {
			clearInterval(this.interval);
		}

		this.showLoading = true;
		this.interval = window.setInterval(() => {
			this.previewPhoto = this.currentAlbumImage.thumb;
			this.retriesLoadImage++;

			if(this.retriesLoadImage >=10){
				this.photoIsAvailable = false;
				this.showLoading = false;
				clearInterval(this.interval);
			}
		}, 1000);
	}


}
